import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';

const ToastContext = createContext(null);

let nextId = 0;

const ICON_MAP = {
  success: Check,
  error: AlertCircle,
  info: Info,
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback((message, options = {}) => {
    const id = ++nextId;
    const variant = options.variant || 'success';
    const duration = options.duration ?? (variant === 'error' ? 0 : variant === 'info' && options.action ? 5000 : 2500);
    const toast = { id, message, variant, action: options.action };
    setToasts((prev) => [...prev, toast]);
    if (duration > 0) {
      const handle = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, handle);
    }
    return id;
  }, [dismiss]);

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach((handle) => clearTimeout(handle));
      t.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications">
        {toasts.map((toast) => {
          const Icon = ICON_MAP[toast.variant] || Check;
          return (
            <div
              key={toast.id}
              className={`toast toast--${toast.variant}`}
              role={toast.variant === 'error' ? 'alert' : 'status'}
              aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
            >
              <Icon size={16} aria-hidden="true" className="toast-icon" />
              <span className="toast-message">{toast.message}</span>
              {toast.action && (
                <button
                  type="button"
                  className="toast-action"
                  onClick={() => {
                    toast.action.onClick();
                    dismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </button>
              )}
              <button
                type="button"
                className="toast-close"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return { show: () => {}, dismiss: () => {} };
  }
  return ctx;
};

export default ToastProvider;
